import { ToggleButtonGroup } from "@mui/material";
import { AirplaneTiltIcon, BuildingsIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import { IconTrips } from "@/components/Icons";
import { CategorySwitcherButton } from "@/features/bookings/components/CategorySwitcher/CategorySwitcherButton";
import {
  rudderAnalytics,
  HotelTrackingEvents,
  FlightTrackingEvents,
  TripTrackingEvents,
} from "@/lib/rudderstack";
import type { SearchScope } from "@/types/types";
import { Feature, isFeatureDisabled } from "@/utils/featureFlags";

interface CategorySwitcherProps {
  current: SearchScope;
}

const eventMap = {
  flights: FlightTrackingEvents.USER_SEARCHED_FOR_ORDERS,
  hotels: HotelTrackingEvents.USER_SEARCHED_FOR_ORDERS,
  trips: TripTrackingEvents.USER_SEARCHED_FOR_ORDERS,
} as const;

export const CategorySwitcher = ({ current }: CategorySwitcherProps) => {
  const t = useTranslations();
  const navigate = useNavigate();

  return (
    <ToggleButtonGroup
      value={current}
      exclusive
      onChange={(_, newValue: SearchScope | null) => {
        if (newValue && eventMap[newValue]) {
          rudderAnalytics.track(eventMap[newValue]);
        }
        navigate({
          to: "/bookings",
          search: {
            category: newValue || current,
          },
          replace: true,
        });
      }}
      aria-label="Service"
      sx={{
        display: "flex",
        gap: "calc(var(--spacing)*2)",
      }}
    >
      <CategorySwitcherButton value="flights">
        <AirplaneTiltIcon size={48} aria-hidden className="shrink-0 p-3" />
        {t("flights.title")}
      </CategorySwitcherButton>

      <CategorySwitcherButton value="hotels">
        <BuildingsIcon size={48} aria-hidden className="shrink-0 p-3" />
        {t("hotels.title")}
      </CategorySwitcherButton>

      {isFeatureDisabled(Feature.TripsFlow) ? null : (
        <CategorySwitcherButton value="trips">
          <IconTrips aria-hidden className="size-12 shrink-0 p-3" />
          {t("trips.title")}
        </CategorySwitcherButton>
      )}
    </ToggleButtonGroup>
  );
};
